import { AgNumberValueCellEditingRenderer } from 'components/AgGrid/AgNumberValueCellEditingRenderer';
import { ButtonWithTooltip } from 'components/WithTooltip';
import { ContractStatusEnum, type BimActKc2TotalItemData, WorkOrderStatusEnum, ProjectStatusEnum } from 'gql/graphql';
import { IconRevert } from 'uikit';

import type { ICellRendererParams } from 'ag-grid-community';
import { useCallback } from 'react';

import Styles from '../BimWorkOrderActsKc2.module.css';
import type { IActTotalDetailsProps } from '../types';

export const ActKc2TotalDetailsSmrCell = (
    props: ICellRendererParams<BimActKc2TotalItemData> & Omit<IActTotalDetailsProps, 'updateLimitedHandler'>,
) => {
    const {
        value = '',
        formatValue,
        data,
        actKC2Id,
        resetCalculatedHandler,
        setInputValueHandler,
        contractStatus,
        workOrderStatus,
        statusProject,
        isVersion,
        isActionDisabled,
        isReadOnly,
        isCommitting,
        ...rest
    } = props;

    const { isLimitPrice, isSelected, isEditable, isInputValue } = data ?? {};
    const actualValue = formatValue?.(value);

    const isContractStatusDelayed = contractStatus === ContractStatusEnum.Delayed;
    const isContractStatusCompleted = contractStatus === ContractStatusEnum.Completed;

    const isWorkOrderStatusDelayed = workOrderStatus === WorkOrderStatusEnum.Delayed;
    const isWorkOrderStatusCompleted = workOrderStatus === WorkOrderStatusEnum.Completed;

    const isProjectStatusDelayed = statusProject === ProjectStatusEnum.Delayed;

    const actionsDisabled =
        isContractStatusDelayed ||
        isContractStatusCompleted ||
        isWorkOrderStatusDelayed ||
        isWorkOrderStatusCompleted ||
        isProjectStatusDelayed ||
        isVersion ||
        isActionDisabled;

    const onClickResetHandler = useCallback(() => {
        resetCalculatedHandler({
            actKC2Id,
            actKC2TotalItemId: data?.totalId,
        });
    }, [actKC2Id, data?.totalId, resetCalculatedHandler]);

    const onChangeHandler = useCallback(
        (newInputValue: number | null) => {
            setInputValueHandler({
                actKC2Id,
                value: newInputValue ?? 0,
                actKC2TotalItemId: data?.totalId,
            });
        },
        [actKC2Id, data?.totalId, setInputValueHandler],
    );

    const isCheckedInflation = !!(data?.isInflation && data?.workOrderActiveSeasonId !== data?.workOrderSeasonId);

    if ((isLimitPrice && isSelected) || (isEditable && !isLimitPrice)) {
        if (isCheckedInflation) {
            return null;
        }

        if (isReadOnly) {
            return actualValue;
        }

        return (
            <div className={Styles.container}>
                <div className={Styles.innerBlock}>
                    {isInputValue && (
                        <ButtonWithTooltip
                            buttonProps={{
                                view: 'clear',
                                size: 's',
                                iconLeft: IconRevert,
                                onClick: onClickResetHandler,
                                disabled: actionsDisabled,
                            }}
                            tooltipProps={{
                                mode: actionsDisabled ? 'click' : 'mouseover',
                                content: 'Сбросить до исходных значений',
                            }}
                        />
                    )}

                    <AgNumberValueCellEditingRenderer
                        isEditable
                        idKey={data?.id}
                        columnKey="smrTotal"
                        data={data}
                        value={value}
                        decimalScale={0}
                        onChange={onChangeHandler}
                        debounceTime={800}
                        view="clear"
                        width="full"
                        disabled={actionsDisabled}
                        isCommitting={isCommitting}
                        {...rest}
                    />
                </div>
            </div>
        );
    }

    return actualValue;
};

ActKc2TotalDetailsSmrCell.displayName = 'ActKc2TotalDetailsSmrCell';
